
















































































































































































































import Vue from "vue";
import { mapState } from "vuex";
import { Toast } from "vant";
import moment from "moment";
import { dispatch, Product, Cart, CartProduct } from "@/store";
import { normalizeDiscountedAmount } from "@/utils/orderAmount";

interface Address {
  province: string;
  city: string;
  district: string;
  detail: string;
  consignee: string;
  phone: string;
}

interface PendingOrder {
  address_id: number;
  user_coupon_id?: number;
  products: any[];
  address: Address;
  max_coin_discount?: number;
  from_cart: boolean;
}

interface Settings {
  shipping: {
    is_open: boolean;
    amount: number;
    threshold: number;
  };
}

// cartCalc 接口返回的数据结构
interface CartCalcResponse {
  actual_amount: number;
  discount_amount: number; // 优惠券抵扣金额
  coin_exchange_amount: number; // MIAOCOIN抵扣金额
  total_amount: number;
  products: any[];
}

// 扩展 Vue 实例类型
interface ComponentData {
  moment: typeof moment;
  loading: boolean;
  matchCoupons: any[];
  pendingOrder: PendingOrder;
  captchaInstance: any; // 阿里云验证码实例
  discount: number; // 优惠券折扣（前端计算）
  useCoinNumber: number;
  maxCoinDiscount: number;
  showCoinDiscountDialog: boolean;
  selectedCoinOption: "all" | "custom" | "";
  customCoinNumber: number; // 自定义输入框的值
  customCoinInput: string; // 用于输入框显示的独立变量（字符串类型支持空值）
  cartCalcResult: CartCalcResponse | null;
  currentOrderKey: string;
}

interface ComponentComputed {
  pendingOrder: PendingOrder;
  settings: Settings;
  couponInfo: string;
  isVirtual: boolean;
  isCoinPay: boolean;
  total: number;
  totalDiscount: number;
  originalTotal: number;
  totalEntity: number;
  shipping: number;
  isShippingOpen: boolean;
  shippingThreshold: number;
}

interface ComponentMethods {
  getMedia: (product: Cart) => any;
  onSubmit: () => void;
  initCaptcha: () => Promise<void>;
  onPlaceOrder: (data: any) => void;
  onProductClick: (product: Product) => void;
  onCoinDiscountChange: () => void;
  calcCoinDiscount: () => Promise<void>;
  calcFinalPrice: () => Promise<void>;
  onSelectCoinOption: (option: "all" | "custom") => void;
  onCoinDiscountConfirm: () => void;
  onCoinDiscountCancel: () => void;
  openCoinDiscountDialog: () => void;
  getUserMiaocoinBalance: (maxDiscountFromCart: number) => Promise<void>;
  initCartCalcResult: () => Promise<void>;
  clearMiaocoinSelection: () => Promise<void>;
  generateOrderKey: () => string;
  syncActionSheetState: () => void;
  onCustomCoinInputChange: (event: any) => void;
  onCustomCoinInputBlur: () => void;
  activated: () => void;
}

type ComponentInstance = Vue &
  ComponentData &
  ComponentComputed &
  ComponentMethods & {
    $router: any;
    $paths: any;
    $store: any;
  };

export default Vue.extend({
  data() {
    return {
      moment,
      loading: true, // 初始为true，等待MIAOCOIN余额获取完成
      matchCoupons: [] as any,
      captchaInstance: null as any,
      discount: 0, // 优惠券折扣
      useCoinNumber: 0,
      maxCoinDiscount: 0,
      showCoinDiscountDialog: false,
      selectedCoinOption: "" as "all" | "custom" | "",
      customCoinNumber: 0,
      customCoinInput: "", // 用于输入框显示的独立变量
      cartCalcResult: null as CartCalcResponse | null,
      currentOrderKey: "" as string,
      lastCouponId: undefined as number | undefined, // 记录上次的优惠券ID
    };
  },
  computed: {
    ...mapState({ settings: "settings" }),
    ...mapState("orders", {
      pendingOrder: "pendingOrder",
    }),
    couponInfo(this: ComponentInstance): string {
      if (this.pendingOrder.user_coupon_id) {
        // 使用后端计算的优惠券抵扣金额（现在 discount_amount 只表示优惠券抵扣）
        if (this.cartCalcResult && this.cartCalcResult.discount_amount > 0) {
          return `-￥${this.cartCalcResult.discount_amount}`;
        }

        // 如果没有cartCalcResult，尝试从matchCoupons中查找（备用方案）
        const currentCoupon = this.matchCoupons.find(
          (c) => c.user_coupon.id === this.pendingOrder.user_coupon_id
        );
        if (currentCoupon?.user_coupon?.save_amount) {
          return `-￥${currentCoupon.user_coupon.save_amount}`;
        }

        return "-￥0";
      } else {
        if (this.matchCoupons.length) {
          return this.matchCoupons.length + " 张可用";
        }
        return "暂无可用";
      }
    },
    isVirtual(this: ComponentInstance): boolean {
      return (
        this.pendingOrder.products.filter((i: any) => i.product.is_virtual)
          .length === this.pendingOrder.products.length
      );
    },
    isCoinPay(this: ComponentInstance): boolean {
      return this.pendingOrder.products[0]?.product?.is_coin_pay;
    },
    total(this: ComponentInstance): number {
      // 完全使用后端 calc 接口的 actual_amount
      return normalizeDiscountedAmount(this.cartCalcResult?.actual_amount || 0);
    },
    // 总折扣金额（用于显示预计优惠金额）
    totalDiscount(this: ComponentInstance): number {
      // 总抵扣 = 优惠券抵扣 + MIAOCOIN抵扣
      const couponDiscount = this.cartCalcResult?.discount_amount || 0;
      const coinDiscount = this.cartCalcResult?.coin_exchange_amount || 0;
      return couponDiscount + coinDiscount;
    },
    // 检查MIAOCOIN数据是否已加载，避免重新加载的视觉效果
    miaocoinDataLoaded(this: ComponentInstance): boolean {
      return this.$store.getters["orders/isMiaocoinDataLoaded"](
        this.currentOrderKey
      );
    },
    // 原始总金额
    originalTotal(this: ComponentInstance): number {
      // 完全使用后端 calc 接口的 total_amount
      return this.cartCalcResult?.total_amount || 0;
    },
    totalEntity(this: ComponentInstance): number {
      let total = 0;
      this.pendingOrder.products.map((item: any) => {
        console.log("item", item);
        if (!item.product.is_virtual)
          total +=
            item.number * (this.isCoinPay ? item.sku.coin : item.sku.amount);
      });
      return total;
    },
    shipping(this: ComponentInstance): number {
      if (this.isCoinPay) return 0;
      const { is_open, amount, threshold } = this.settings.shipping;
      if (is_open && this.totalEntity && this.totalEntity < threshold) {
        return amount;
      } else if (!is_open && this.totalEntity && this.totalEntity < 100) {
        return 10;
      } else {
        return 0;
      }
    },
    isShippingOpen(this: ComponentInstance): boolean {
      return this.settings.shipping.is_open;
    },
    shippingThreshold(this: ComponentInstance): number {
      return this.settings.shipping.threshold;
    },
  },
  methods: {
    getMedia(this: ComponentInstance, product: any) {
      if (product.sku.medias && product.sku.medias[0]) {
        return product.sku.medias[0];
      } else {
        return product.product.main_medias[0];
      }
    },
    onVerifyOpenedOld(this: ComponentInstance) {
      const NoCaptcha = (window as any).NoCaptcha;

      const nc_token = [
        "FFFF0N00000000009B12",
        new Date().getTime(),
        Math.random(),
      ].join(":");
      const nc = NoCaptcha.init({
        renderTo: "#nc",
        appkey: "FFFF0N00000000009B12",
        scene: "nc_other_h5",
        token: nc_token,
        trans: { key1: "code200" },
        elementID: ["usernameID"],
        is_Opt: 0,
        language: "cn",
        timeout: 10000,
        retryTimes: 5,
        errorTimes: 5,
        inline: false,
        apimap: {
          // 'analyze': '//a.com/nocaptcha/analyze.jsonp',
          // 'uab_Url': '//aeu.alicdn.com/js/uac/909.js',
        },
        bannerHidden: false,
        initHidden: false,
        callback: (data: any) => {
          this.onPlaceOrder({
            session_id: data.csessionid,
            sign: data.sig,
            token: nc_token,
            scene: "nc_other_h5",
          });
          setTimeout(() => {
            // this.showVerify = false;
          }, 150);
        },
      });
      NoCaptcha.setEnabled(true);
      nc.reset(); //请务必确保这里调用一次reset()方法
      //用于配置滑动验证的自定义文案。详细信息，请参见自定义文案与多语言文档。
      NoCaptcha.upLang("cn", {
        //加载状态提示。
        LOADING: "loading...",
        //等待滑动状态提示。
        SLIDER_LABEL: "请向右滑动验证下单",
        //验证通过状态提示。
        CHECK_Y: "验证通过",
        //验证失败触发拦截状态提示。
        ERROR_TITLE: "验证失败，请重新验证",
      });
    },
    onSubmit(this: ComponentInstance) {
      // 每次点击提交都重新初始化验证码，确保每次都需要重新验证
      this.initCaptcha();
    },
    initCaptcha(this: ComponentInstance) {
      try {
        const pageWidth = window.innerWidth;
        const isMobile = pageWidth <= 768; // 判断是否为移动端
        
        // 计算rem比例：移动端根据375px基准自适应，PC端固定为1
        const customWidth = 375;
        const remScale = isMobile 
          ? Math.min(pageWidth / customWidth, 1) // 移动端最大不超过1
          : 1; // PC端固定为1
        
        (window as any).initAliyunCaptcha({
          // 场景ID
          SceneId: "y349l147",
          // 验证码模式，popup表示弹出式
          mode: "popup",
          language: "cn",
          
          // 根据设备类型设置不同的尺寸，PC端占满宽度留边距
          slideStyle: {
            width: isMobile ? 300 : Math.min(pageWidth - 80, 600),
            height: isMobile ? 40 : 60,
          },
          
          // 确保弹窗居中显示
          popupStyle: {
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          },
          
          // rem自适应：移动端按比例缩放，PC端保持原始大小
          rem: remScale,
          // 验证码验证通过回调函数
          success: (captchaVerifyParam: any) => {
            console.log('验证通过:', captchaVerifyParam);
            this.onPlaceOrder({
              captchav_verify_param: captchaVerifyParam,
              scene_id: "y349l147",
            });
            
            // 验证通过并下单后，清空实例引用，确保下次提交需要重新验证
            this.captchaInstance = null;
          },
          
          // 验证码验证失败回调函数
          fail: (result: any) => {
            console.error('验证失败:', result);
            // 验证失败也清空实例，下次重新验证
            this.captchaInstance = null;
          },
          
          // 绑定验证码实例回调函数
          getInstance: (instance: any) => {
            this.captchaInstance = instance;
            console.log('验证码实例已初始化，立即显示');
            // 初始化完成后立即显示验证码弹窗
            instance.show();
          },
        });
      } catch (error) {
        console.error('验证码初始化失败:', error);
        Toast('验证码初始化失败，请刷新页面重试');
      }
    },
    onPlaceOrder(this: ComponentInstance, data: any) {
      Toast.allowMultiple(true);
      const toast = Toast.loading({
        message: "下单中...",
        duration: 0,
        forbidClick: true,
      });
      const productsShare = JSON.parse(
        localStorage.getItem("products-share") || "{}"
      );
      this.pendingOrder.products.map((item: CartProduct) => {
        item.share_code = productsShare[item.product_id] || "";
      });

      // 构建下单数据，包含MIAOCOIN使用数量
      const orderData: any = {
        ...this.pendingOrder,
        captcha: data,
      };

      // 如果使用了MIAOCOIN，添加use_coin_number参数
      if (this.useCoinNumber > 0) {
        orderData.use_coin_number = this.useCoinNumber;
      }

      console.log("下单数据:", orderData);

      dispatch
        .ordersPost(orderData as any)
        .then((res) => {
          toast.clear();

          // 订单成功后清理当前订单的 MIAOCOIN 选择状态
          this.$store.commit(
            "orders/CLEAR_MIAOCOIN_SELECTION",
            this.currentOrderKey
          );

          this.$router.replace(this.$paths.orders);
          setTimeout(() => {
            this.$router.push(this.$paths.orders + "/" + res.order.order_no);
          }, 100);
        })
        .catch(() => {
          toast.clear();
        });
    },
    onProductClick(this: ComponentInstance, product: any) {
      this.$router.push(this.$paths.products + "/" + product.product.id);
    },
    onCoinDiscountChange(this: ComponentInstance) {
      // 限制输入范围
      if (this.useCoinNumber < 0) {
        this.useCoinNumber = 0;
      }
      if (this.useCoinNumber > this.maxCoinDiscount) {
        this.useCoinNumber = this.maxCoinDiscount;
      }

      // 调用 calc 接口计算优惠
      this.calcCoinDiscount();
    },
    async calcCoinDiscount(this: ComponentInstance) {
      // 直接调用最终价格计算，包含所有参数
      await this.calcFinalPrice();
    },
    async calcFinalPrice(this: ComponentInstance) {
      // 计算包含所有优惠的最终价格（MIAOCOIN + 优惠券）
      try {
        const products = this.pendingOrder.products.map((item: any) => ({
          number: item.number,
          product_id: item.product_id,
          sku_id: item.sku_id,
          share_code: item.share_code || "",
        }));

        const calcData: any = {
          products: products,
        };

        // 包含所有优惠参数
        if (this.useCoinNumber > 0) {
          calcData.use_coin_number = this.useCoinNumber;
        }

        if (this.pendingOrder.user_coupon_id) {
          calcData.user_coupon_id = this.pendingOrder.user_coupon_id;
        }

        const res = await this.$store.dispatch("cart/calc", calcData);
        console.log("最终价格计算响应:", res);

        // 保存完整的最终价格结果
        this.cartCalcResult = {
          actual_amount: res.actual_amount || 0,
          discount_amount: res.discount_amount || 0,
          coin_exchange_amount: res.coin_exchange_amount || 0,
          total_amount: res.total_amount || 0,
          products: res.products || [],
        };
      } catch (error) {
        console.error("计算最终价格失败:", error);
      }
    },
    onSelectCoinOption(this: ComponentInstance, option: "all" | "custom") {
      this.selectedCoinOption = option;
      if (option === "all") {
        // 选择全部可用时，不影响自定义输入框的值
        // customCoinInput 保持独立
      } else {
        // 选择自定义时，如果输入框为空且当前有使用数量，则设置输入框值
        if (
          this.customCoinInput === "" &&
          this.useCoinNumber > 0 &&
          this.useCoinNumber !== this.maxCoinDiscount
        ) {
          this.customCoinInput = this.useCoinNumber.toString();
        }
      }
    },
    onCoinDiscountConfirm(this: ComponentInstance) {
      let finalAmount = 0;
      if (this.selectedCoinOption === "all") {
        finalAmount = this.maxCoinDiscount;
      } else if (this.selectedCoinOption === "custom") {
        const inputValue = parseInt(this.customCoinInput) || 0;
        finalAmount = Math.min(Math.max(inputValue, 0), this.maxCoinDiscount);
        // 确认后将输入框的值保存到customCoinNumber用于状态记录
        this.customCoinNumber = inputValue;
      }

      this.useCoinNumber = finalAmount;

      // 保存 MIAOCOIN 选择状态到 store（与当前订单绑定，包含选择类型）
      console.log(
        "保存MIAOCOIN状态:",
        this.currentOrderKey,
        finalAmount,
        this.selectedCoinOption
      );
      this.$store.commit("orders/SET_MIAOCOIN_SELECTION", {
        orderId: this.currentOrderKey, // 用唯一的订单标识
        amount: finalAmount,
        type: this.selectedCoinOption, // 保存用户的选择类型
        customAmount: this.customCoinNumber, // 保存自定义输入的数量
      });
      console.log(
        "当前store状态:",
        this.$store.state.orders.miaocoinSelections
      );

      // 重新计算包含所有优惠的最终价格
      this.calcFinalPrice();
      this.showCoinDiscountDialog = false;
    },
    onCoinDiscountCancel(this: ComponentInstance) {
      this.showCoinDiscountDialog = false;
      this.selectedCoinOption = "";
      // 取消时不重置自定义输入框的值，保持用户输入
    },
    openCoinDiscountDialog(this: ComponentInstance) {
      // 在打开ActionSheet时同步内部状态
      this.syncActionSheetState();
      this.showCoinDiscountDialog = true;
    },
    async getUserMiaocoinBalance(
      this: ComponentInstance,
      maxDiscountFromCart: number
    ) {
      try {
        console.log("获取用户MIAOCOIN余额...");

        // 调用getBalance接口获取用户余额
        const res = await this.$store.dispatch("profile/getBalance");
        console.log("用户余额响应:", res);

        // 查找MIAOCOIN余额
        let userMiaocoinBalance = 0;
        if (res.count_list && Array.isArray(res.count_list)) {
          const miaocoinItem = res.count_list.find(
            (item: any) => item.coin_name === "MIAOCOIN"
          );
          if (miaocoinItem) {
            userMiaocoinBalance = miaocoinItem.sum || 0;
          }
        }

        console.log("用户MIAOCOIN余额:", userMiaocoinBalance);
        console.log("购物车max_coin_discount:", maxDiscountFromCart);

        // 计算实际可用的MIAOCOIN数量：取用户余额和购物车限制的最小值
        this.maxCoinDiscount = Math.min(
          userMiaocoinBalance,
          maxDiscountFromCart
        );

        console.log("最终可用MIAOCOIN数量:", this.maxCoinDiscount);

        // 标记MIAOCOIN数据已加载，避免后续重新加载的视觉效果
        this.$store.commit("orders/SET_MIAOCOIN_DATA_LOADED", {
          orderId: this.currentOrderKey,
          loaded: true,
        });
      } catch (error) {
        console.error("获取MIAOCOIN余额失败:", error);
        // 失败时使用购物车传递的值作为备用
        this.maxCoinDiscount = 0;
      } finally {
        // 完成余额获取后设置loading为false，显示MIAOCOIN选项
        this.loading = false;

        // 注意：MIAOCOIN 选择在页面初始化时已清空，每次都是全新开始
      }
    },
    async initCartCalcResult(this: ComponentInstance) {
      // 调用后端 calc 接口初始化价格信息（包含所有当前状态）
      try {
        const products = this.pendingOrder.products.map((item: any) => ({
          number: item.number,
          product_id: item.product_id,
          sku_id: item.sku_id,
          share_code: item.share_code || "",
        }));

        const calcData: any = {
          products: products,
        };

        // 如果有优惠券，加上优惠券参数
        if (this.pendingOrder.user_coupon_id) {
          calcData.user_coupon_id = this.pendingOrder.user_coupon_id;
        }

        // 如果有MIAOCOIN使用量，加上MIAOCOIN参数
        if (this.useCoinNumber > 0) {
          calcData.use_coin_number = this.useCoinNumber;
        }

        const res = await this.$store.dispatch("cart/calc", calcData);
        console.log("初始化 cartCalc 响应:", res);

        // 保存完整的 cartCalc 结果
        this.cartCalcResult = {
          actual_amount: res.actual_amount || 0,
          discount_amount: res.discount_amount || 0,
          coin_exchange_amount: res.coin_exchange_amount || 0,
          total_amount: res.total_amount || 0,
          products: res.products || [],
        };
      } catch (error) {
        console.error("初始化价格计算失败:", error);
        // 失败时设置默认值
        this.cartCalcResult = {
          actual_amount: 0,
          discount_amount: 0,
          coin_exchange_amount: 0,
          total_amount: 0,
          products: [],
        };
      }
    },

    generateOrderKey: function (this: ComponentInstance): string {
      // 生成唯一的订单标识，结合地址ID、商品信息
      const addressId = this.pendingOrder.address_id;
      const productInfo = this.pendingOrder.products
        .map((item: any) => `${item.sku.id}_${item.number}`)
        .join("|");
      return `${addressId}_${productInfo}`;
    },

    async clearMiaocoinSelection(this: ComponentInstance) {
      this.useCoinNumber = 0;
      // 清除当前订单的 MIAOCOIN 选择状态
      this.$store.commit(
        "orders/CLEAR_MIAOCOIN_SELECTION",
        this.currentOrderKey
      );
      // 重新计算最终价格（不含MIAOCOIN）
      await this.calcFinalPrice();
    },

    // 同步ActionSheet内部状态与useCoinNumber
    syncActionSheetState(this: ComponentInstance) {
      if (this.useCoinNumber > 0) {
        // 从store获取完整的选择信息（包含类型）
        const savedSelection = this.$store.getters[
          "orders/getMiaocoinSelection"
        ](this.currentOrderKey);

        if (savedSelection && savedSelection.type) {
          // 优先使用保存的选择类型
          this.selectedCoinOption = savedSelection.type;

          if (savedSelection.type === "custom" && savedSelection.customAmount) {
            // 恢复自定义输入框的值
            this.customCoinNumber = savedSelection.customAmount;
            this.customCoinInput = savedSelection.customAmount.toString();
          }
        } else {
          // 降级逻辑：根据数量判断（但优先保持自定义）
          if (
            this.useCoinNumber === this.maxCoinDiscount &&
            !this.selectedCoinOption
          ) {
            // 只有在没有明确选择时，才自动判断为"全部使用"
            this.selectedCoinOption = "all";
          } else {
            this.selectedCoinOption = "custom";
            this.customCoinNumber = this.useCoinNumber;
            this.customCoinInput = this.useCoinNumber.toString();
          }
        }
      } else {
        this.selectedCoinOption = "";
        this.customCoinNumber = 0;
        this.customCoinInput = "";
      }
    },

    // 处理自定义输入框的输入事件
    onCustomCoinInputChange(this: ComponentInstance, event: any) {
      const inputValue = event.target.value;

      // 允许空值（用户可以清空输入框）
      if (
        inputValue === "" ||
        inputValue === null ||
        inputValue === undefined
      ) {
        this.customCoinInput = "";
        return;
      }

      let value = parseInt(inputValue) || 0;

      // 限制输入范围：不能小于0，不能大于最大可用数量
      if (value < 0) {
        value = 0;
        event.target.value = value.toString();
      }
      if (value > this.maxCoinDiscount) {
        value = this.maxCoinDiscount;
        event.target.value = value.toString();
      }

      // 更新输入框的值
      this.customCoinInput = value.toString();
    },

    // 处理输入框失去焦点事件
    onCustomCoinInputBlur(this: ComponentInstance) {
      // 失去焦点时，如果输入框为空，允许保持为空
      if (this.customCoinInput === "") {
        return;
      }

      // 将字符串转换为数字进行验证
      let value = parseInt(this.customCoinInput) || 0;

      // 只有在输入了无效值时才进行修正
      if (value < 0) {
        value = 0;
      }
      if (value > this.maxCoinDiscount) {
        value = this.maxCoinDiscount;
      }

      // 将修正后的值转换回字符串
      this.customCoinInput = value.toString();
    },
  },

  // 页面激活时（从优惠券选择页面返回时）
  activated(this: ComponentInstance) {
    // 确保currentOrderKey已设置
    if (!this.currentOrderKey) {
      this.currentOrderKey = this.generateOrderKey();
    }

    // 恢复MIAOCOIN选择状态（如果之前有保存的状态）
    const savedSelection = this.$store.getters["orders/getMiaocoinSelection"](
      this.currentOrderKey
    );
    if (
      savedSelection &&
      savedSelection.amount > 0 &&
      this.useCoinNumber !== savedSelection.amount
    ) {
      this.useCoinNumber = savedSelection.amount;
      console.log("页面激活时恢复MIAOCOIN选择状态:", savedSelection);

      // 同步ActionSheet内部状态（现在会使用保存的type信息）
      this.syncActionSheetState();

      // 如果恢复了MIAOCOIN状态，重新计算价格
      this.calcFinalPrice();
    }
  },

  created(this: ComponentInstance) {
    if (this.pendingOrder.address_id) {
      // 生成更精确的订单标识（包含商品信息），避免不同商品组合的订单互相干扰
      this.currentOrderKey = this.generateOrderKey();

      // 检查并恢复当前订单的 MIAOCOIN 选择状态
      console.log("检查MIAOCOIN状态 orderKey:", this.currentOrderKey);
      console.log(
        "store中的selections:",
        this.$store.state.orders.miaocoinSelections
      );
      const savedSelection = this.$store.getters["orders/getMiaocoinSelection"](
        this.currentOrderKey
      );
      console.log("获取到的savedSelection:", savedSelection);
      if (savedSelection && savedSelection.amount > 0) {
        this.useCoinNumber = savedSelection.amount;
        console.log("恢复MIAOCOIN选择状态:", savedSelection);
      }

      // 获取从 Cart 页面传递过来的 max_coin_discount
      console.log("PlaceOrder pendingOrder:", this.pendingOrder);
      console.log(
        "max_coin_discount from pendingOrder:",
        this.pendingOrder.max_coin_discount
      );

      // 检查是否已加载过MIAOCOIN数据
      const miaocoinLoaded = this.$store.getters["orders/isMiaocoinDataLoaded"](
        this.currentOrderKey
      );

      // 先设置初始值，然后通过余额接口获取实际可用数量
      const initialMaxDiscount = this.pendingOrder.max_coin_discount || 0;

      if (miaocoinLoaded) {
        // 如果已加载过，直接设置为不loading状态，避免重新加载的视觉效果
        this.loading = false;
        this.maxCoinDiscount = initialMaxDiscount; // 使用购物车传递的值作为临时显示
        console.log("MIAOCOIN数据已加载，跳过loading状态");
      }

      // 获取用户MIAOCOIN余额（即使已加载过，也在后台更新以确保数据准确性）
      this.getUserMiaocoinBalance(initialMaxDiscount);

      // 获取匹配的优惠券
      dispatch
        .couponsMatchUserCoupon(this.pendingOrder as any)
        .then((res: any) => {
          res.results.map((item: any) => {
            if (item.is_match) {
              this.matchCoupons.push(item);
            }
          });
          // matchCoupons 加载完成，couponInfo 计算属性会自动更新显示
        });

      // 统一的价格计算（包含所有状态：MIAOCOIN + 优惠券）
      this.initCartCalcResult();

      // 初始化ActionSheet状态同步
      this.syncActionSheetState();
    } else {
      this.$router.replace(this.$paths.cart);
    }
  },
});
